import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  DollarSign, 
  AlertTriangle,
  Menu,
  TrendingUp,
  Users,
  Star
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Sidebar } from '../components/Sidebar';
import { useBots } from '@/contexts/BotsContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { getStrategyLabel } from '../utils/strategies';
import { formatPct, formatCurrency } from '../utils/format';
import { cn } from '../utils/cn';
import { toast } from 'sonner';

export const SellBotPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bots } = useBots();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bot, setBot] = useState<any>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isFree, setIsFree] = useState(false);
  const [price, setPrice] = useState<number | ''>('');
  const [visibility, setVisibility] = useState<'public' | 'unlisted' | 'private'>('public');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const botId = searchParams.get('botId');

  useEffect(() => {
    if (!botId || !user) return;
    
    const loadBot = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('bots')
          .select('id, name, description, strategy, strategy_key, exchange, quote_currency, profit, total_trades, win_rate, is_practice')
          .eq('id', botId)
          .eq('user_id', user.id)
          .single();
          
        if (error) {
          console.error('Error loading bot:', error);
          toast.error('Failed to load bot');
          navigate('/my-bots');
          return;
        }
        
        setBot(data);
        setTitle(data?.name || '');
        
        // Pre-fill description with bot info
        const strategyLabel = getStrategyLabel(data?.strategy, data?.strategy_key);
        const defaultDescription = `High-performing ${strategyLabel} trading bot with proven results. ` +
          `Optimized for ${data?.exchange || 'multiple exchanges'} trading with ${data?.quote_currency || 'USDT'} pairs.`;
        setDescription(defaultDescription);
        
      } catch (error) {
        console.error('Error loading bot:', error);
        toast.error('Failed to load bot');
        navigate('/my-bots');
      } finally {
        setLoading(false);
      }
    };
    
    loadBot();
  }, [botId, user, navigate]);

  const handleSaveDraft = async () => {
    if (!user || !botId) return;
    
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('f_marketplace_upsert_listing_v1', {
        p_user_id: user.id,
        p_bot_id: botId,
        p_title: title || bot?.name || 'My bot',
        p_description: description || '',
        p_is_free: !!isFree,
        p_price_usd: isFree ? null : (price || 0),
        p_visibility: visibility,
        p_kind: isFree ? 'donation' : 'sale',
      });
      
      if (error) {
        console.error('Error saving draft:', error);
        toast.error('Failed to save draft');
        return;
      }
      
      const listingId = typeof data === 'string' ? data : data?.id;
      toast.success('Saved as draft');
      navigate(`/marketplace/listing/${listingId}`);
      
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublish = async () => {
    if (!user || !botId) return;
    
    if (!description.trim()) {
      toast.error('Please provide a description for your bot');
      return;
    }
    
    if (!isFree && (!price || price < 1)) {
      toast.error('Listing price must be at least $1');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // First upsert the listing
      const { data, error } = await supabase.rpc('f_marketplace_upsert_listing_v1', {
        p_user_id: user.id,
        p_bot_id: botId,
        p_title: title || bot?.name || 'My bot',
        p_description: description || '',
        p_is_free: !!isFree,
        p_price_usd: isFree ? null : (price || 0),
        p_visibility: visibility,
        p_kind: isFree ? 'donation' : 'sale',
      });
      
      if (error) {
        console.error('Error saving listing:', error);
        toast.error('Failed to save listing');
        return;
      }
      
      const listingId = typeof data === 'string' ? data : data?.id;
      
      // Then publish it
      const { error: publishError } = await supabase.rpc('f_marketplace_publish_listing_v1', {
        p_listing_id: listingId,
        p_user_id: user.id,
      });
      
      if (publishError) {
        console.error('Error publishing listing:', publishError);
        toast.error('Failed to publish listing');
        return;
      }
      
      toast.success('Published to Marketplace!');
      navigate(`/marketplace/listing/${listingId}`);
      
    } catch (error) {
      console.error('Error publishing listing:', error);
      toast.error('Failed to publish listing');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading bot...</p>
        </div>
      </div>
    );
  }

  if (!bot || !botId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Bot Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">The bot you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/my-bots')}>
            Back to My Bots
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
      <div className="flex">
        {/* Mobile Menu Button */}
        <div className="md:hidden fixed top-20 left-4 z-50">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700"
          >
            <Menu className="h-6 w-6 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        {/* Sidebar */}
        <Sidebar 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          isSidebarCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
        />

        {/* Main content */}
        <div className={`flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
          <main className="p-4 md:p-8 mt-8 md:mt-0">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="max-w-4xl mx-auto space-y-6"
            >
              {/* Header */}
              <div className="flex items-center space-x-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/my-bots')}
                  className="flex items-center space-x-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to My Bots</span>
                </Button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Sell Bot on Marketplace
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400">
                    List your bot for other traders to purchase and use
                  </p>
                </div>
              </div>

              {/* Bot Preview */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Bot Preview</h2>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Bot ID: {botId}</div>
                </div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Bot Preview</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {bot.title || bot.name || 'Unnamed Bot'}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Strategy:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {getStrategyLabel(bot.strategy, bot.strategy_key)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Exchange:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {bot.exchange || 'Unknown'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Quote Currency:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {bot.quote_currency || 'USDT'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Performance:</span>
                        <span className={cn(
                          "font-medium",
                          (bot.profit || 0) >= 0 ? "text-green-600" : "text-red-600"
                        )}>
                          {formatPct(bot.profit)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-5 w-5 text-blue-500" />
                        <span className="font-medium text-blue-700 dark:text-blue-400">Performance Highlights</span>
                      </div>
                      <ul className="text-sm text-blue-600 dark:text-blue-300 space-y-1">
                        <li>• Total Trades: {bot.total_trades || 0}</li>
                        <li>• Win Rate: {bot.win_rate ? bot.win_rate.toFixed(1) : '0.0'}%</li>
                        <li>• Profit: {formatPct(bot.profit)}</li>
                        <li>• Mode: {bot.is_practice ? 'Paper Trading' : 'Live Trading'}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Listing Form */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Listing Details</h2>
                
                <div className="space-y-6">
                  {/* Title */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Listing Title
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter listing title"
                    />
                  </div>

                  {/* Free/Paid Toggle */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={isFree}
                          onChange={(e) => setIsFree(e.target.checked)}
                        />
                        <div className={cn(
                          "w-11 h-6 rounded-full peer",
                          "bg-gray-200 peer-focus:outline-none peer-focus:ring-4",
                          "peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800",
                          "dark:bg-gray-600 peer-checked:after:translate-x-full",
                          "peer-checked:after:border-white after:content-['']",
                          "after:absolute after:top-[2px] after:left-[2px]",
                          "after:bg-white after:border-gray-300 after:border",
                          "after:rounded-full after:h-5 after:w-5",
                          "after:transition-all dark:border-gray-600",
                          "peer-checked:bg-blue-600"
                        )}></div>
                      </label>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Free (Donation)
                      </span>
                    </div>
                  </div>

                  {/* Price */}
                  {!isFree && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Listing Price (USD)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          value={price}
                          onChange={(e) => setPrice(e.target.value === '' ? '' : Number(e.target.value))}
                          min="1"
                          step="0.01"
                          className="w-full px-4 py-2 pl-8 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter listing price"
                        />
                        <DollarSign className="absolute left-2.5 top-2.5 h-5 w-5 text-gray-400" />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Recommended price range: $19.99 - $99.99 based on bot performance
                      </p>
                    </div>
                  )}

                  {/* Visibility */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Visibility
                    </label>
                    <select
                      value={visibility}
                      onChange={(e) => setVisibility(e.target.value as any)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="public">Public</option>
                      <option value="unlisted">Unlisted</option>
                      <option value="private">Private</option>
                    </select>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Bot Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="Describe your bot's strategy, performance, and unique features..."
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {description.length}/500 characters
                    </p>
                  </div>

                  {/* Marketplace Info */}
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800/30">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-400 mb-1">
                          Marketplace Guidelines
                        </p>
                        <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                          <li>• Your bot configuration will be copied for buyers</li>
                          <li>• Buyers will need to configure their own API keys</li>
                          <li>• You'll receive 70% of the sale price (30% platform fee)</li>
                          <li>• Bot performance history will be visible to potential buyers</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end space-x-4">
                    <Button
                      variant="outline"
                      onClick={() => navigate('/my-bots')}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleSaveDraft}
                      isLoading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      Save as Draft
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handlePublish}
                      isLoading={isSubmitting}
                      icon={<DollarSign className="h-4 w-4" />}
                      iconPosition="left"
                    >
                      Publish to Marketplace
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </main>
        </div>

        {/* Mobile menu overlay */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default SellBotPage;